import { ref } from 'vue';
import RealtimeKitClient from '@cloudflare/realtimekit';
import { createRemoteAudioPlayer } from 'shared/helpers/realtimeKitAudio';

/**
 * Shared audio-only Cloudflare RealtimeKit integration point. Used by the
 * AudioCallPanel, the public call page app, and the widget call overlay.
 */
export const useRealtimeKitAudio = () => {
  const state = ref('idle');
  const errorMessage = ref('');
  const muted = ref(false);
  const remotePeerName = ref(null);
  let meeting = null;
  let detachRemoteAudio = null;

  const bindEvents = boundMeeting => {
    boundMeeting.self.on('roomLeft', () => {
      // Guard against a stale meeting still firing events after cleanup
      // (e.g. a failed join or a superseded double-connect) has already
      // detached it as the current session.
      if (meeting !== boundMeeting) return;
      state.value = 'ended';
    });
    boundMeeting.participants.joined.on('participantJoined', participant => {
      // Same stale-meeting guard as roomLeft above — a superseded session's
      // late participant events must not clobber the current one's peer name.
      if (meeting !== boundMeeting) return;
      remotePeerName.value = participant.name;
    });
    boundMeeting.participants.joined.on('participantLeft', () => {
      if (meeting !== boundMeeting) return;
      remotePeerName.value = null;
    });
  };

  const cleanup = async () => {
    detachRemoteAudio?.();
    detachRemoteAudio = null;
    if (meeting) {
      try {
        await meeting.leave();
      } catch (error) {
        // Best-effort: leaving a dead/dangling session should never block
        // cleanup or surface an error to the caller.
      }
    }
    meeting = null;
    remotePeerName.value = null;
    muted.value = false;
  };

  const connect = async authToken => {
    if (meeting) await cleanup();
    errorMessage.value = '';
    state.value = 'connecting';
    try {
      meeting = await RealtimeKitClient.init({
        authToken,
        defaults: { audio: true, video: false },
      });
      bindEvents(meeting);
      await meeting.join();
      detachRemoteAudio = createRemoteAudioPlayer(meeting);
      state.value = 'connected';
    } catch (error) {
      if (meeting) await cleanup();
      errorMessage.value = error.message;
      state.value = 'error';
      throw error;
    }
  };

  const disconnect = async () => {
    await cleanup();
    state.value = 'ended';
  };

  const toggleMute = async () => {
    if (!meeting) return;
    if (muted.value) {
      await meeting.self.enableAudio();
      muted.value = false;
    } else {
      await meeting.self.disableAudio();
      muted.value = true;
    }
  };

  return {
    state,
    errorMessage,
    muted,
    remotePeerName,
    connect,
    disconnect,
    toggleMute,
  };
};
